import { NoteFields } from "./helpers";
import { LineItem, Note } from "./order";
import { buildLineDescription } from "./line_description";

/**
 * FUPOF-191 — the one place a line's NetSuite production-notes string is built
 * and taken apart again.
 *
 * The cart keeps a line's notes structured: free text on `LineItem.notes`, and
 * the per-field configurator notes on `item_details.notes`. NetSuite keeps a
 * single flat string on `custcol_productionnotes`. `composeLineNotes` flattens
 * to NetSuite's shape; `decomposeLineNotes` recovers the structured shape.
 *
 * The two must stay exact inverses, and the composed string must never be
 * written back into either of the inputs it was built from. That is what broke
 * before: the DriftResolver adopted NetSuite's flat string straight onto
 * `LineItem.notes`, and the next submit composed *that* into the production
 * notes — carrying the item's description into NetSuite. Anything reading a
 * flat NetSuite string into the cart goes through `decomposeLineNotes` first.
 *
 * Only submit composes today. `quote_lines.notes` stores the rep's free text
 * as-is, so nothing on the draft save/load path composes or decomposes; the
 * notes drift that mismatch causes is parked in the store's `reloadFromNs`.
 */

const ITEM_NOTES_HEAD = "Item Notes: ";

/**
 * FUPOF-191 — heading for the spelled-out configuration block that submit prepends
 * to a custom line's production notes (see buildLineDescription). It is derived data,
 * so it is never written back into the cart: decomposeLineNotes recognizes and drops
 * this block rather than folding it into freeText.
 */
const CONFIG_HEAD = "Configuration:";

/** Maps the wire heading back to its field: "BOTTOMPOLE" -> "bottomPole". */
const FIELD_BY_HEAD = new Map<string, NoteFields>(
  Object.values(NoteFields).map(f => [f.toUpperCase(), f] as [string, NoteFields])
);

const HEADS = ["Item", ...Array.from(FIELD_BY_HEAD.keys())];

/**
 * A segment ends only where a line break is followed by a known heading — free
 * text may itself span several lines, so nothing else terminates it.
 */
const SEGMENT_BOUNDARY = new RegExp(`\\n(?=(?:${HEADS.join("|")}) Notes: )`);

export interface DecomposedLineNotes {
  /** The line's own free text (`LineItem.notes`). */
  freeText: string;
  /** The per-field configurator notes (`item_details.notes`). */
  fieldNotes: Note[];
}

/**
 * Flattens a line's notes into the single string NetSuite stores.
 *
 * `configSource` is the line to spell the Configuration block out of, and passing
 * it is what opts the line in. Two reasons it is a separate argument rather than
 * being derived from `item`:
 *
 * - Only lines NetSuite does not recognize (TEMP-CUST-ITEM) need their configuration
 *   spelled out; the caller is the one holding the SKU lookup result, so the caller
 *   decides.
 * - By submit time `item` has been through OrderReview.parseItems(), which flattens
 *   canopy fabric swatches to bare mfr codes — exactly the shape buildLineDescription
 *   refuses to render. The caller passes the store's untouched line instead.
 */
export const composeLineNotes = (
  item: LineItem,
  configSource?: LineItem | null
): string => {
  // An ns_locked line's notes are opaque free-form text adopted verbatim from
  // NetSuite (there is no configurator to structure them; the whole value lives
  // in the General block, see recoverLockedLineNotes). Send it back exactly as
  // received — no per-field "X Notes:" headings — so a resubmit can't alter
  // NetSuite's own production-notes string, grow a wrapper around it, or split
  // it into boxes on the next adopt.
  if (item.ns_locked) {
    return [item.notes ?? "", ...(item.item_details?.notes ?? []).map(n => n.content)]
      .filter(Boolean)
      .join("\n");
  }
  const segments: string[] = [];
  if (item.notes) {
    segments.push(`${ITEM_NOTES_HEAD}${item.notes}`);
  }
  for (const note of item.item_details?.notes ?? []) {
    if (!note.content) continue;
    segments.push(`${note.field.toUpperCase()} Notes: ${note.content}`);
  }
  // Prepend the spelled-out configuration so NetSuite's production notes name the
  // custom (TEMP-CUST-ITEM) product. It is derived, not authored — decompose drops
  // it, so it never round-trips back into the cart's notes.
  const description = configSource ? buildLineDescription(configSource) : "";
  if (description) {
    segments.unshift(`${CONFIG_HEAD}\n${description}`);
  }
  return segments.join("\n");
};

/** The inverse of `composeLineNotes`. */
export const decomposeLineNotes = (
  composed?: string | null
): DecomposedLineNotes => {
  const result: DecomposedLineNotes = { freeText: "", fieldNotes: [] };
  if (!composed) return result;

  // NetSuite stores multi-line production notes with CRLF; the segment split
  // keys off `\n`, so normalize first or a stray `\r` clings to the tail of
  // each recovered segment's content.
  const normalized = composed.replace(/\r\n?/g, "\n");

  let recognized = false;
  for (const segment of normalized.split(SEGMENT_BOUNDARY)) {
    // The Configuration block is derived from the line's configuration on submit;
    // drop it here so it never lands in freeText and re-grows on the next submit.
    // Marking it recognized also stops the whole string being treated as free text.
    if (segment.startsWith(CONFIG_HEAD)) {
      recognized = true;
      continue;
    }
    if (segment.startsWith(ITEM_NOTES_HEAD)) {
      result.freeText = segment.slice(ITEM_NOTES_HEAD.length);
      recognized = true;
      continue;
    }
    const match = /^([A-Z]+) Notes: ([\s\S]*)$/.exec(segment);
    const field = match ? FIELD_BY_HEAD.get(match[1]) : undefined;
    if (match && field) {
      result.fieldNotes.push({ field, content: match[2] });
      recognized = true;
    }
  }

  // Nothing matched, so the string was authored outside the Quote Builder —
  // production notes typed straight into NetSuite on an adopted line, say.
  // Keep it verbatim as free text instead of dropping it on the floor.
  if (!recognized) result.freeText = normalized;

  return result;
};
